import { Tooltip, TooltipProps } from '@material-ui/core';
import { Sync } from '@material-ui/icons';
import { Tag } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useMemo } from 'react';
import styled from 'styled-components';
import { useFlag } from 'shared/settings/FeatureToggles';
import { getLoadStatusColor } from '../core/getLoadStatusColor';
import type { LoadDTO } from '../data/LoadDTO';
import { getLoadInspectionPhotoSyncCountsFromLoad } from './loadInspectionSyncFromLoad';
import { LoadInspectionSyncTooltipContentWrapper } from './LoadInspectionSyncTooltipContent';
import { useLoadInspectionSyncTooltipAutoShow } from './useLoadInspectionSyncTooltipAutoShow';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .MuiTooltip-tooltip`]: {
    maxWidth: 500,
  },
});

const TriggerRoot = styled.div`
  .MuiSvgIcon-root {
    margin-top: -2px;
    vertical-align: middle;
  }
`;

interface LoadInspectionSyncHeaderStatusProps {
  load: LoadDTO;
  statusLabel: string;
  shouldDisplaySyncStatus?: boolean;
}

export function LoadInspectionSyncHeaderStatus({
  load,
  statusLabel,
  shouldDisplaySyncStatus = false,
}: LoadInspectionSyncHeaderStatusProps) {
  const isPhotoSync = useFlag('ctms_photo_sync');
  const counts = useMemo(
    () => getLoadInspectionPhotoSyncCountsFromLoad(load),
    [load],
  );

  const hasPendingPhotosFromLoad =
    counts.total > 0 && counts.synced < counts.total;

  const isDriverUploadingPhoto =
    hasPendingPhotosFromLoad || shouldDisplaySyncStatus;

  const isEligibleForSyncTooltip =
    isPhotoSync &&
    (load.status === 'picked_up' || load.status === 'delivered') &&
    isDriverUploadingPhoto;

  const {
    isTooltipOpen,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    onTooltipClose,
  } = useLoadInspectionSyncTooltipAutoShow({
    loadGuid: load.guid,
    isEligible: isEligibleForSyncTooltip,
    enableAutoShow: isEligibleForSyncTooltip && !shouldDisplaySyncStatus,
  });

  if (!isEligibleForSyncTooltip) {
    return (
      <Tag
        aria-label="status tag"
        variant="subtle"
        color={getLoadStatusColor(load.status)}
      >
        {statusLabel}
      </Tag>
    );
  }

  return (
    <Tag aria-label="status tag" variant="subtle" color="grey">
      <CustomWidthTooltip
        placement="top"
        arrow={true}
        title={
          <LoadInspectionSyncTooltipContentWrapper
            loadGuid={load.guid}
            loadStatus={load.status}
            synced={counts.synced}
            total={counts.total}
            shouldFetchFreshCounts={shouldDisplaySyncStatus}
            isTooltipOpen={isTooltipOpen}
          />
        }
        open={isTooltipOpen}
        onOpen={onTriggerMouseEnter}
        onClose={onTooltipClose}
        disableFocusListener={true}
        disableHoverListener={true}
        disableTouchListener={false}
      >
        <TriggerRoot
          onMouseEnter={onTriggerMouseEnter}
          onMouseLeave={onTriggerMouseLeave}
        >
          <Box display="inline" marginRight="xxsmall">
            <Sync fontSize="small" color="action" />
          </Box>
          {statusLabel}
        </TriggerRoot>
      </CustomWidthTooltip>
    </Tag>
  );
}
